import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
} from '@mui/material';
import React, { useMemo } from 'react';
import { isPeriodWithinCurrentOrPreviousMonth, ReopenPeriodAlertData } from './ReopenPeriodModal';
import { closeoutTestId } from './test-ids';

type BillingWarningModalProps = {
  open: boolean;
  onClose: () => void;
  alertData: ReopenPeriodAlertData | null;
  onConfirmClose: (alertData: ReopenPeriodAlertData | null) => void;
};

const SCENARIO_A_TITLE = 'Billing Export Notice';

const SCENARIO_A_BODY = (
  <>
    Changes were made to Daily Session Notes or payers after this period was reopened.
    <br />
    <br />
    If you previously received a billing export for this period, you may need to request an updated
    export to reflect the changes.
  </>
);

const SCENARIO_B_TITLE = 'Billing & Report Regeneration Required';

const SCENARIO_B_BODY = (
  <>
    Changes were made to Daily Session Notes or payer information after this period was reopened.
    <br />
    <br />
    If you previously received a billing export for this period, you&apos;ll need to request an
    updated export. You may also need updated UBO4 or SLM reports.
    <br />
    <br />
    Please contact Restore Support via chat to request updated billing exports or reports.
  </>
);

const BillingWarningModal: React.FC<BillingWarningModalProps> = ({
  open,
  onClose,
  alertData,
  onConfirmClose,
}) => {
  const isCurrentOrPreviousMonth = useMemo(() => {
    if (!alertData?.range) return true;
    return isPeriodWithinCurrentOrPreviousMonth(alertData.range);
  }, [alertData?.range?.startDate, alertData?.range?.endDate]);

  const title = isCurrentOrPreviousMonth ? SCENARIO_A_TITLE : SCENARIO_B_TITLE;
  const bodyContent = isCurrentOrPreviousMonth ? SCENARIO_A_BODY : SCENARIO_B_BODY;

  const handleConfirmClose = () => {
    onConfirmClose(alertData);
  };

  return (
    <Dialog
      data-testid={closeoutTestId('billing-warning-dialog')}
      open={open}
      onClose={onClose}
      aria-labelledby="billing-warning-dialog-title"
      aria-describedby="billing-warning-dialog-description"
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
          p: 0,
        },
      }}
    >
      <DialogTitle
        id="billing-warning-dialog-title"
        component="h2"
        sx={{
          fontSize: '1.25rem',
          fontWeight: 600,
          color: 'text.primary',
          pb: 0,
        }}
      >
        {title}
      </DialogTitle>
      <DialogContent>
        <Typography
          id="billing-warning-dialog-description"
          variant="body2"
          sx={{ color: '#6C737F', fontWeight: 400, pt: 2 }}
          component="div"
        >
          {bodyContent}
        </Typography>
      </DialogContent>
      <DialogActions
        sx={{
          px: 3,
          py: 2,
          gap: 1,
        }}
      >
        <Button
          color="inherit"
          onClick={onClose}
          data-testid={closeoutTestId('billing-warning-cancel')}
          sx={{ color: 'text.secondary' }}
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleConfirmClose}
          data-testid={closeoutTestId('billing-warning-confirm')}
          sx={{
            textTransform: 'none',
          }}
        >
          Close Period
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default BillingWarningModal;
